import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import { Check, Facebook, Instagram, Link2, Linkedin, Mail, Send, Share2, X } from "lucide-react";
import { Button } from "@/shared/ui/Button";

interface ShareEntityButtonProps {
  title: string;
  path: string;
  description?: string;
  previewUrl?: string | null;
  className?: string;
  iconOnly?: boolean;
  label?: string;
}

const absoluteUrl = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path;
  if (typeof window === "undefined") return path;
  return new URL(path, window.location.origin).toString();
};

export const ShareEntityButton = ({
  title,
  path,
  description,
  previewUrl,
  className,
  iconOnly = true,
  label = "Ndaj",
}: ShareEntityButtonProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = useMemo(() => absoluteUrl(path), [path]);
  const text = useMemo(() => description ? `${title} - ${description}` : title, [description, title]);
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(`${text} ${url}`);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const nativeShare = async (): Promise<void> => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        setOpen(false);
        return;
      } catch {
        return;
      }
    }
    await copyLink();
  };

  const openInstagram = async (): Promise<void> => {
    await nativeShare();
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size={iconOnly ? "icon" : "sm"}
        className={className}
        title={label}
        aria-label={label}
        onClick={() => setOpen(true)}
      >
        <Share2 className="h-4 w-4" aria-hidden="true" />
        {!iconOnly ? <span>{label}</span> : null}
      </Button>
      {open ? createPortal(
        <div className="fixed inset-0 z-[180] flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-sm" role="presentation" onMouseDown={() => setOpen(false)}>
          <section className="max-h-[calc(100dvh-1.5rem)] w-full max-w-[560px] overflow-y-auto rounded-[24px] border border-border bg-card p-5 shadow-2xl sm:p-6" role="dialog" aria-modal="true" aria-labelledby="share-entity-title" onMouseDown={(event) => event.stopPropagation()}>
            <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
              <div className="min-w-0">
                <h2 id="share-entity-title" className="truncate text-lg font-bold text-foreground">Ndaj</h2>
                <p className="mt-1 text-sm text-muted-foreground">Zgjidh menyren e shperndarjes.</p>
              </div>
              <Button type="button" variant="ghost" size="icon" title="Mbyll" aria-label="Mbyll" onClick={() => setOpen(false)}><X className="h-4 w-4" /></Button>
            </header>
            <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-background/70 p-4">
              {previewUrl ? <img src={previewUrl} alt="" className="mb-3 max-h-48 w-full rounded-xl bg-muted object-contain" /> : null}
              <p className="font-semibold text-foreground">{title}</p>
              {description ? <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{description}</p> : null}
            </div>
            <div className="grid grid-cols-4 gap-3 p-5 sm:grid-cols-5">
              <ShareTarget href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} label="Facebook" icon={<Facebook className="h-5 w-5" />} onClick={() => setOpen(false)} />
              <ShareTarget href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} label="LinkedIn" icon={<Linkedin className="h-5 w-5" />} onClick={() => setOpen(false)} />
              <ShareTarget href={`https://wa.me/?text=${encodedText}`} label="WhatsApp" icon={<MessageIcon label="WA" />} onClick={() => setOpen(false)} />
              <ShareTarget href={`https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(text)}`} label="Telegram" icon={<Send className="h-5 w-5" />} onClick={() => setOpen(false)} />
              <ShareTarget href={`https://twitter.com/intent/tweet?text=${encodedText}`} label="X" icon={<MessageIcon label="X" />} onClick={() => setOpen(false)} />
              <ShareTarget href={`viber://forward?text=${encodedText}`} label="Viber" icon={<MessageIcon label="V" />} onClick={() => setOpen(false)} />
              <ShareTarget href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodedText}`} label="Email" icon={<Mail className="h-5 w-5" />} onClick={() => setOpen(false)} />
              <button type="button" onClick={() => void openInstagram()} className="flex min-w-0 flex-col items-center gap-1.5 rounded-xl p-2 text-xs font-medium text-foreground transition hover:bg-muted" title="Instagram">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-fuchsia-500/10 text-fuchsia-600"><Instagram className="h-5 w-5" /></span><span className="truncate">Instagram</span>
              </button>
              <button type="button" onClick={() => void nativeShare()} className="flex min-w-0 flex-col items-center gap-1.5 rounded-xl p-2 text-xs font-medium text-foreground transition hover:bg-muted" title="MÃ« shumÃ« opsione">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary"><Share2 className="h-5 w-5" /></span><span className="truncate">MÃ« shumÃ«</span>
              </button>
              <button type="button" onClick={() => void copyLink()} className="flex min-w-0 flex-col items-center gap-1.5 rounded-xl p-2 text-xs font-medium text-foreground transition hover:bg-muted" title="Kopjo linkun">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-secondary text-foreground">{copied ? <Check className="h-5 w-5 text-emerald-600" /> : <Link2 className="h-5 w-5" />}</span><span className="truncate">{copied ? "U kopjua" : "Kopjo"}</span>
              </button>
            </div>
          </section>
        </div>,
        document.body,
      ) : null}
    </>
  );
};

function ShareTarget({ href, label, icon, onClick }: { href: string; label: string; icon: JSX.Element; onClick: () => void }): JSX.Element {
  return <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} className="flex min-w-0 flex-col items-center gap-1.5 rounded-xl p-2 text-xs font-medium text-foreground transition hover:bg-muted" title={label}><span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">{icon}</span><span className="truncate">{label}</span></a>;
}

function MessageIcon({ label }: { label: string }): JSX.Element {
  return <span className="text-sm font-bold" aria-hidden="true">{label}</span>;
}
